import { AnalyticIdentifier } from '@common/neo.tax';
import { Button, colors, ModalActions, ModalHeader, Stack, Typography } from '@ui-components/neo.tax';
import { ReactComponent as AlarmBellRing } from '@ui-components/neo.tax/dist/icons/alarm-bell-ring.svg';
import { useEffect, useState } from 'react';

import { useAnalyticId } from '@neo.tax/shared/state';

import { AllocationSlider } from '../allocation-slider';
import { StyledModal, StyledSlidersStack } from './bulk-allocation-modal.styled';

export const BulkAllocationModal = ({
  cancelButtonId,
  copy,
  headerText,
  isOpen,
  loading,
  setIsOpen,
  sliderLabelText: sliderText,
  submit,
  submitButtonId,
}: {
  isOpen: boolean;
  submit: (allocationPercent: number) => Promise<void>;
  loading: boolean;
  setIsOpen: (open: boolean) => void;
  onClose?: () => void;
  copy: React.ReactNode;
  headerText: string;
  sliderLabelText: string;
  cancelButtonId: AnalyticIdentifier;
  submitButtonId: AnalyticIdentifier;
}): React.ReactElement | null => {
  const cancelBtnId = useAnalyticId(cancelButtonId);
  const submitBtnId = useAnalyticId(submitButtonId);
  const [allocationPercent, setAllocationPercent] = useState(100);

  // Reset to 100 when modal opens
  useEffect(() => {
    if (isOpen) {
      setAllocationPercent(100);
    }
  }, [isOpen]);

  const handleChange = (percent: number): void => {
    setAllocationPercent(percent);
  };

  const handleSubmit = (): void => {
    submit(allocationPercent);
  };

  return (
    <StyledModal isOpen={isOpen} setIsOpen={setIsOpen}>
      <Stack>
        <Stack fullWidth direction="column" paddingX={6} paddingY={6}>
          <ModalHeader
            icon={<AlarmBellRing width={24} color={colors.blue[600]} />}
            iconBgColor={colors.blue[50]}
            title={
              <Typography fontSize="base" fontWeight="semibold">
                {headerText}
              </Typography>
            }
            copy={copy}
          />
          <StyledSlidersStack direction="column" fullWidth>
            <AllocationSlider allocationPercent={allocationPercent} label={sliderText} onChange={handleChange} />
          </StyledSlidersStack>

          <ModalActions hasTopBorder={false}>
            <Button
              id={cancelBtnId}
              disabled={loading}
              variant="white"
              rounded="regular"
              size="base"
              onClick={(): void => setIsOpen(false)}
            >
              Cancel
            </Button>
            <Button
              id={submitBtnId}
              disabled={loading}
              variant="primary"
              rounded="regular"
              size="base"
              onClick={handleSubmit}
            >
              {loading ? 'Applying...' : 'Apply'}
            </Button>
          </ModalActions>
        </Stack>
      </Stack>
    </StyledModal>
  );
};
